import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, MessageSquare, CircleHelp, Flag,
  Sparkles, Mail, Hash, Clock, Store, ExternalLink,
} from 'lucide-react';
import NotificationBellButton from '../components/NotificationBellButton';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { useBackTarget } from '../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = 'var(--accent-purple)';
const BRAND_DARK = 'var(--accent-text-soft)';
const SUPPORT_EMAIL = 'contact@allurexp.com';

const ENTRY_DELAY = 0.06;

const supportItems = [
  {
    id: 'contact',
    label: 'Contact Support',
    description: 'Chat directly with our team',
    icon: MessageSquare,
    route: '/support/contact',
    iconBg: 'rgba(196, 18, 48, 0.08)',
    iconColor: '#9B7BFF',
  },
  {
    id: 'faq',
    label: 'FAQ',
    description: 'Browse common questions',
    icon: CircleHelp,
    route: '/support/faq',
    iconBg: 'rgba(247, 167, 0, 0.16)',
    iconColor: '#F0B046',
  },
  {
    id: 'report-venue',
    label: 'Report a Venue',
    description: 'Flag inappropriate content',
    icon: Flag,
    route: '/support/report-venue',
    iconBg: 'rgba(229, 57, 53, 0.16)',
    iconColor: '#FF6B61',
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Share an idea or improvement',
    icon: Sparkles,
    route: '/support/feedback',
    iconBg: 'rgba(46, 230, 160, 0.16)',
    iconColor: '#4FE08A',
  },
];

function _relativeTime(iso) {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return '';
  }
}

export default function AllureSupportPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const greeting = user.first_name ? `Hi ${user.first_name}` : 'Hey there';

  // Venue owners get a dedicated card that jumps to the external partner
  // FAQ hosted on the marketing site. Multi-role users (e.g. owners who
  // are currently viewing as guest) still see it — the availability check
  // covers both the active role and any secondary role they can switch to.
  const isVenueOwner = user?.role === 'venue_owner'
    || (Array.isArray(user?.available_roles) && user.available_roles.includes('venue_owner'));

  const openVenuePartnerFaq = () => {
    // `_system` forces Capacitor Android to open the URL in the external
    // browser (Chrome) instead of an in-app WebView. On the web PWA this
    // is treated as a normal new-tab open.
    window.open('https://allurexp.com/for-venues', '_system');
  };

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { setLoading(false); return; }
      const res = await axios.get(`${API}/support/requests/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setThreads(res.data || []);
    } catch (_) {
      // silent — the empty state still works
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const openThreads = (threads || []).filter(
    (t) => (t.status || 'open') !== 'solved',
  );
  const latestOpen = openThreads[0] || null;
  const latestMessage =
    latestOpen?.messages?.[latestOpen.messages.length - 1]?.content || '';

  return (
    <div
      className="app-shell premium-page"
      data-testid="allure-support-page"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
        backgroundImage:
          'radial-gradient(ellipse 600px 400px at 50% -150px, rgba(196, 18, 48, 0.08) 0%, transparent 60%)',
      }}
    >
      {/* Header bar */}
      <div
        style={{
          width: '100%',
          maxWidth: 390,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0 0',
        }}
      >
        <button
          onClick={goBack}
          data-testid="allure-support-back-button"
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-border)',
            borderRadius: 999,
            cursor: 'pointer',
            padding: 6,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: 'var(--premium-shadow-nested)',
            transition: 'transform 180ms ease',
          }}
          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          <ChevronLeft size={22} color="var(--text)" strokeWidth={2} />
        </button>
        <NotificationBellButton
          buttonTestId="allure-support-bell-button"
          popupTestId="allure-support-notification-popup"
        />
      </div>

      {/* Logo + heading */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        <RouteTransitionLogo
          width={108}
          height={96}
          dataTestId="allure-support-shared-logo"
          style={{ marginTop: 22 }}
        />
        <p
          data-testid="allure-support-greeting"
          style={{
            margin: '14px 0 0',
            fontFamily: SUB_FONT,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: BRAND,
            opacity: 0.85,
          }}
        >
          {greeting}
        </p>
        <h1
          style={{
            fontFamily: FONT,
            fontSize: 30,
            fontWeight: 400,
            color: 'var(--text)',
            margin: '4px 0 0',
            letterSpacing: '-0.01em',
            textAlign: 'center',
          }}
        >
          How can we help?
        </h1>
        <p
          style={{
            margin: '8px 0 0',
            fontFamily: SUB_FONT,
            fontSize: 13.5,
            color: 'var(--text-2)',
            textAlign: 'center',
            maxWidth: 300,
            lineHeight: 1.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Clock size={13} strokeWidth={2} color={BRAND} />
          Real humans · usually replying within 2 hours
        </p>
      </motion.div>

      {/* Active conversation card */}
      {!loading && latestOpen && (
        <motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: ENTRY_DELAY }}
          data-testid="allure-support-active-thread"
          onClick={() =>
            navigate('/support/contact', {
              state: { threadId: latestOpen.id, reason: latestOpen.reason },
            })
          }
          style={{
            width: '100%',
            maxWidth: 360,
            marginTop: 24,
            background:
              'var(--accent-purple)',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: 22,
            padding: '16px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            cursor: 'pointer',
            boxShadow: 'var(--premium-shadow-main)',
            textAlign: 'left',
            transition: 'transform 200ms ease, box-shadow 200ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--premium-shadow-main)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--premium-shadow-main)';
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <MessageSquare size={20} color="#FFFFFF" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: SUB_FONT,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                opacity: 0.75,
              }}
            >
              Active conversation
            </p>
            <p
              style={{
                margin: '3px 0 0',
                fontFamily: FONT,
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {latestOpen.reason || 'Open thread'}
            </p>
            <p
              style={{
                margin: '4px 0 0',
                fontFamily: SUB_FONT,
                fontSize: 12,
                opacity: 0.85,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {latestMessage ? `“${latestMessage}”` : 'Tap to view the conversation'}
              {latestOpen.updated_at && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  · {_relativeTime(latestOpen.updated_at)}
                </span>
              )}
            </p>
          </div>
          <ChevronRight size={20} color="#FFFFFF" strokeWidth={2} />
        </motion.button>
      )}

      {/* Section label */}
      <p
        style={{
          width: '100%',
          maxWidth: 360,
          margin: '28px 0 12px',
          fontFamily: SUB_FONT,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          color: 'var(--text-2)',
        }}
      >
        What&apos;s on your mind?
      </p>

      {/* Reason grid */}
      <div
        style={{
          width: '100%',
          maxWidth: 360,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
        }}
      >
        {supportItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <motion.button
              key={item.id}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1 + index * 0.05 }}
              onClick={() => navigate(item.route)}
              data-testid={`support-menu-${item.id}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                textAlign: 'left',
                gap: 8,
                background: 'var(--surface)',
                border: '1px solid var(--card-border)',
                borderRadius: 20,
                padding: '16px 14px',
                minHeight: 134,
                cursor: 'pointer',
                boxShadow: 'var(--premium-shadow-nested)',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = 'var(--premium-shadow-main)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--premium-shadow-nested)';
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: item.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon size={18} color={item.iconColor || 'var(--text)'} strokeWidth={2} />
              </div>
              <span
                style={{
                  fontFamily: FONT,
                  fontSize: 17,
                  fontWeight: 700,
                  color: 'var(--text)',
                  lineHeight: 1.15,
                  marginTop: 4,
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  fontFamily: SUB_FONT,
                  fontSize: 12,
                  color: 'var(--text-2)',
                  lineHeight: 1.35,
                }}
              >
                {item.description}
              </span>
            </motion.button>
          );
        })}
      </div>

      <div style={{ flex: 1, minHeight: 18 }} />

      {/* Venue Partner FAQ — owner-only shortcut to the marketing site's
          venue-partners page. Opens in the external browser so the venue
          owner can dig through the full Q&A without losing their spot
          in the app. */}
      {isVenueOwner ? (
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.35 }}
          data-testid="support-menu-venue-partner-faq"
          onClick={openVenuePartnerFaq}
          style={{
            width: '100%',
            maxWidth: 360,
            marginTop: 4,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            background: 'linear-gradient(135deg, rgba(112, 63, 246, 0.14), rgba(112, 63, 246, 0.05))',
            border: '1px solid rgba(112, 63, 246, 0.22)',
            borderRadius: 20,
            padding: '14px 16px',
            cursor: 'pointer',
            boxShadow: 'var(--premium-shadow-nested)',
            textAlign: 'left',
            transition: 'transform 200ms ease, box-shadow 200ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = 'var(--premium-shadow-main)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'var(--premium-shadow-nested)';
          }}
        >
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: 'rgba(112, 63, 246, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Store size={18} color={BRAND} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                margin: 0,
                fontFamily: FONT,
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--text)',
                lineHeight: 1.2,
              }}
            >
              Venue Partner FAQ
            </p>
            <p
              style={{
                margin: '3px 0 0',
                fontFamily: SUB_FONT,
                fontSize: 12.5,
                color: 'var(--text-2)',
                lineHeight: 1.35,
              }}
            >
              Answers for Allure venue owners · opens in browser
            </p>
          </div>
          <ExternalLink size={16} color={BRAND} strokeWidth={2.2} />
        </motion.button>
      ) : null}

      {/* Account footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        style={{
          width: '100%',
          maxWidth: 360,
          marginTop: 14,
          padding: '14px 16px',
          background: 'var(--surface)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid var(--card-border)',
          borderRadius: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="allure-support-email-link"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
            color: 'var(--accent-text-soft)',
            fontFamily: SUB_FONT,
            fontSize: 13.5,
            fontWeight: 600,
          }}
        >
          <Mail size={15} strokeWidth={2.2} color="var(--accent-text-soft)" />
          {SUPPORT_EMAIL}
        </a>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12.5,
            color: 'var(--text-2)',
          }}
          data-testid="support-account-id"
        >
          <Hash size={13} strokeWidth={2.2} color="var(--text-2)" />
          Account · {user.customer_id || '—'}
        </div>
      </motion.div>
    </div>
  );
}
